import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { User, CheckCircle, Package, FilterIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { buscarEmpleadoPorCedula } from '@/pages/parametrizacion/employees/Services/employees.services';
import { createRecoleccion, getRecolecciones } from '@/pages/deposito/services/deposito.services';
import { previewThermalReceipt } from '@/utils/thermalPrinter';
import ModalEntregaRopa from './ModalEntregaRopa';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import { entregasColumns, processEntregaData } from './utils/entregasColumns';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'pendiente_verificacion' };

const EntregaRopa = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [cedula, setCedula] = useState('');
  const [empleado, setEmpleado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [entregaExitosa, setEntregaExitosa] = useState(false);
  
  // Estados para la tabla
  const [entregas, setEntregas] = useState([]);
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoadingTable, setIsLoadingTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleBuscarEmpleado = async () => {
    if (!cedula.trim()) {
      toast({
        title: 'Error',
        description: 'Ingrese el número de cédula',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await buscarEmpleadoPorCedula(cedula);
      if (response?.data) {
        setEmpleado(response.data);
        setShowModal(true);
/*         toast({
          title: 'Empleado encontrado',
          description: `${response.data.nombre} - ${response.data.area?.nombre || 'Sin área'}`
        }); */
      } else {
        toast({
          title: 'No encontrado',
          description: 'No se encontró un empleado con esa cédula',
          variant: 'destructive'
        });
        setEmpleado(null);
      }
    } catch (error) {
      console.error('Error buscando empleado:', error);
      toast({
        title: 'Error',
        description: 'No se pudo buscar el empleado',
        variant: 'destructive'
      });
      setEmpleado(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrarEntrega = async (prendas) => {
    if (!empleado) {
      toast({
        title: 'Error',
        description: 'Debe buscar un empleado primero',
        variant: 'destructive'
      });
      throw new Error('No hay empleado seleccionado');
    }

    try {
      // Crear la recolección sin asignar casillero
      const recoleccionData = {
        planta: empleado.planta ? {
          id: empleado.planta.id || empleado.planta._id,
          nombre: empleado.planta.nombre
        } : undefined,
        empleado: {
          id: empleado._id || empleado.id,
          nombre: empleado.nombre,
          apellido: empleado.apellido,
          cedula: empleado.cedula,
          celular: empleado.celular || '',
          area: empleado.area ? {
            id: empleado.area.id || empleado.area._id,
            nombre: empleado.area.nombre
          } : undefined,
          planta: empleado.planta ? {
            id: empleado.planta.id || empleado.planta._id,
            nombre: empleado.planta.nombre
          } : undefined,
          centroCosto: empleado.centroCosto ? {
            id: empleado.centroCosto.id || empleado.centroCosto._id,
            nombre: empleado.centroCosto.nombre
          } : undefined
        },
        casillero: 'N/A', // Valor por defecto ya que no se asigna casillero en autoservicio
        prendas: prendas.map(p => ({
          idPrenda: p.idPrenda,
          nombre: p.nombre,
          descripcion: p.descripcion,
          cantidad: p.cantidad,
          observaciones: p.observaciones || '',
          peso: p.peso || 0
        })),
        cantidad_prendas: prendas.reduce((sum, p) => sum + p.cantidad, 0),
        estado: 'pendiente_verificacion'
      };
      const response = await createRecoleccion(recoleccionData);

      // Verificar si la respuesta indica un error
      if (!response || response.state === false || response.code >= 400) {
        const errorMessage = response?.message || response?.data?.message || 'Error al crear la recolección';
        throw new Error(errorMessage);
      }

      toast({
        title: '¡Entrega recibida!',
        description: `Se ha registrado la entrega de ${recoleccionData.cantidad_prendas} prenda(s)`,
        duration: 5000
      });

      // Mostrar mensaje de éxito
      setEntregaExitosa(true);
      setShowModal(false);

      // Limpiar formulario después de 3 segundos
      setTimeout(() => {
        setCedula('');
        setEmpleado(null);
        setEntregaExitosa(false);
      }, 3000);

      // Recargar la tabla de entregas
      fetchEntregas();

    } catch (error) {
      console.error('Error registrando entrega:', error);
      
      // Extraer mensaje de error más específico
      let errorDescription = 'No se pudo registrar la entrega';
      
      if (error.message) {
        errorDescription = error.message;
      } else if (error.response?.data?.message) {
        errorDescription = error.response.data.message;
      }
      
      toast({
        title: 'Error al registrar entrega',
        description: errorDescription,
        variant: 'destructive'
      });
      throw error;
    }
  };

  // Función para cargar las entregas
  const fetchEntregas = useCallback(async () => {
    setIsLoadingTable(true);
    try {
      const response = await getRecolecciones({
        limit: ITEMS_PER_PAGE,
        offset: currentPage,
        estado: filters.estado,
        cedula: filters['empleado.cedula']
      });
      
      const data = response?.data || [];
      const total = response?.totalRecords || 0;
      
      setEntregas(data);
      setDisplayedData(processEntregaData(data));
      setTotalItems(total);
    } catch (error) {
      console.error('Error cargando entregas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingTable(false);
    }
  }, [currentPage, filters, toast]);

  useEffect(() => {
    fetchEntregas();
  }, [fetchEntregas]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setIsDrawerOpen(false);
  };

  const handleRemoveFilter = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleAction = (actionType, row) => {
    if (actionType === 'preview') {
      // Buscar la entrega completa en el array de entregas
      const entrega = entregas.find(e => {
        const eId = e._id || e.id;
        return eId === row.id || eId === row._id;
      });

      if (entrega) {
        try {
          previewThermalReceipt(entrega);
          toast({
            title: 'Vista Previa',
            description: 'Abriendo vista previa del recibo...'
          });
        } catch (error) {
          console.error('Error generando vista previa:', error);
          toast({
            title: 'Error',
            description: 'No se pudo generar la vista previa',
            variant: 'destructive'
          });
        }
      } else {
        toast({
          title: 'Error',
          description: 'No se encontró la información de la entrega',
          variant: 'destructive'
        });
      }
    } else if (actionType === 'view') {
      toast({ 
        title: 'Ver Entrega', 
        description: `Visualizando entrega de ${row['empleado.nombre']}` 
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Buscar Empleado */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-500" />
            Registrar prendas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Número de documento</Label>
              <Input
                type="text"
                placeholder="Ingrese el número de documento"
                value={cedula}
                onChange={(e) => setCedula(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleBuscarEmpleado()}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handleBuscarEmpleado} disabled={loading}>
                Buscar
              </Button>
            </div>
          </div>

          {empleado && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
            >
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-green-900 dark:text-green-100">
                  Empleado Encontrado
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Nombre:</span>
                  <span className="ml-2 font-medium">{empleado.nombre}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Cédula:</span>
                  <span className="ml-2 font-medium">{empleado.cedula}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Área:</span>
                  <span className="ml-2 font-medium">{empleado.area?.nombre || 'Sin área'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Celular:</span>
                  <span className="ml-2 font-medium">{empleado.celular || 'No registrado'}</span>
                </div>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Entrega de Ropa */}
      <ModalEntregaRopa
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        empleado={empleado}
        onRegistrar={handleRegistrarEntrega}
      />

      {/* Confirmación de Entrega */}
      {entregaExitosa && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <Card className="border-2 border-green-500 bg-green-50 dark:bg-green-900/20">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
                <div>
                  <h3 className="text-2xl font-bold text-green-900 dark:text-green-100">
                    ¡Entrega Recibida Correctamente!
                  </h3>
                  <p className="text-muted-foreground mt-2">
                    La ropa ha sido registrada exitosamente
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Tabla de Entregas Recientes */}
      <Card>
        <CardHeader className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="w-full md:w-auto">
            <div className="flex items-center space-x-3 mb-2">
              <Package className="h-6 w-6 text-primary" />
              <CardTitle className="text-xl">Entregas Recientes</CardTitle>
            </div>
            <CardDescription>
              Historial de entregas de ropa registradas
            </CardDescription>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setIsDrawerOpen(true)}>
              <FilterIcon className="mr-2 h-4 w-4" />
              Filtros
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="w-full">
            <AppliedFilters
              filters={filters}
              fields={[
                {
                  id: 'estado',
                  label: 'Estado',
                  type: 'select',
                  options: [
                    { value: 'recibido', label: 'Recibido' },
                    { value: 'pendiente_verificacion', label: 'Pendiente Verificación' },
                    { value: 'entregado', label: 'Entregado' }
                  ]
                },
                { id: 'empleado.cedula', label: 'Cédula', type: 'text' }
              ]}
              onRemoveFilter={handleRemoveFilter}
            />
          </div>

          <DataTable
            data={displayedData}
            columns={entregasColumns}
            isLoading={isLoadingTable}
            onAction={handleAction}
            showCreateButton={false}
          />

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />

          <FilterDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            fields={[
              {
                id: 'estado',
                label: 'Estado',
                type: 'select',
                options: [
                  { value: 'recibido', label: 'Recibido' },
                  { value: 'pendiente_verificacion', label: 'Pendiente Verificación' },
                  { value: 'verificado', label: 'Verificado' },
                  { value: 'rechazado', label: 'Rechazado' },
                  { value: 'entregado', label: 'Entregado' }
                ]
              },
              { id: 'empleado.cedula', label: 'Cédula Empleado', type: 'text' }
            ]}
            initialFilters={filters}
            onChange={handleFilterChange}
            onApply={handleFilterChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default EntregaRopa;
